// The unauthenticated entry screen. AuthGuard renders this inline (it is not a
// route), so sign-up and email verification live here as view state rather
// than as separate pages. Keeps the cotty branding shell; swaps the inner card
// between sign-in, sign-up, OTP verification, and password reset.

import { ArrowLeft, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { authClient } from '../../lib/auth-client';
import { isDesktop, setBearer } from '../../lib/desktop-token';
import { toast } from '../../stores/toastStore';
import { CottyBanner, CtosBackdrop, HudFrame } from '../cottyFx';
import {
    AuthError,
    authErrorMessage,
    authPersist,
    transportErrorMessage,
    useCooldown,
    BTN_LINK,
    BTN_PRIMARY,
    FIELD_INPUT,
    LABEL,
    OtpField,
} from './authShared';

type View = 'signin' | 'signup' | 'verify' | 'reset';

const isView = (v: string | null): v is View =>
    v === 'signin' || v === 'signup' || v === 'verify' || v === 'reset';

function GoogleIcon({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
        </svg>
    );
}

// On verification / password-reset the session cookie (web) is freshly set;
// reload so AuthGuard re-evaluates and drops us into the app. Clear the
// persisted auth step first so a future sign-out starts cleanly at sign-in.
const enterApp = () => {
    authPersist.clear();
    window.location.reload();
};

export function AuthScreen({ notice }: { notice?: React.ReactNode } = {}) {
    // Restore the step + email from the tab session so a remount/reload (e.g.
    // the alt-tab session refetch) keeps the user on the OTP step rather than
    // bouncing them to sign-in and spawning a new — code-invalidating — OTP.
    const [view, setView] = useState<View>(() => {
        const v = authPersist.view();
        return isView(v) ? v : 'signin';
    });
    // Email is shared so moving sign-up → verify (or sign-in → verify) keeps it.
    const [email, setEmail] = useState(() => authPersist.email());

    useEffect(() => {
        authPersist.save(view, email);
    }, [view, email]);

    return (
        <div className="relative min-h-screen w-full grid place-items-center bg-bg-0 overflow-hidden">
            <CtosBackdrop vignette={false} />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-60"
                style={{
                    background:
                        'radial-gradient(60% 50% at 50% 28%, var(--accent-soft) 0%, transparent 60%), radial-gradient(40% 40% at 80% 80%, rgba(255,181,120,0.08) 0%, transparent 70%)',
                }}
            />

            <div className="relative w-full max-w-100 mx-4 flex flex-col items-center gap-7 py-10">
                <div className="cotty-banner-hero">
                    <CottyBanner />
                </div>

                {notice}

                <div className="relative w-full">
                    <div className="relative rounded-2xl border border-border bg-bg-1/80 backdrop-blur-sm shadow-[0_8px_40px_-12px_rgba(0,0,0,0.6)] p-8">
                        <HudFrame sweep={false} />
                        {view === 'signin' && (
                            <SignInCard
                                email={email}
                                setEmail={setEmail}
                                goTo={setView}
                            />
                        )}
                        {view === 'signup' && (
                            <SignUpCard
                                email={email}
                                setEmail={setEmail}
                                goTo={setView}
                            />
                        )}
                        {view === 'verify' && (
                            <VerifyCard email={email} goTo={setView} />
                        )}
                        {view === 'reset' && (
                            <ResetCard
                                email={email}
                                setEmail={setEmail}
                                goTo={setView}
                            />
                        )}
                    </div>

                    <p className="mt-4 text-center text-[11px] text-text-faint">
                        Trouble signing in?{' '}
                        <a href="mailto:support@cotty.sh" className={BTN_LINK}>
                            Get help
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}

// ─── sign in ─────────────────────────────────────────────────────────────────

function SignInCard({
    email,
    setEmail,
    goTo,
}: {
    email: string;
    setEmail: (v: string) => void;
    goTo: (v: View) => void;
}) {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onGoogle = async () => {
        setError(null);
        setGoogleLoading(true);
        try {
            if (isDesktop() && window.cotty?.signInWithGoogle) {
                const { token } = await window.cotty.signInWithGoogle();
                setBearer(token);
                window.location.reload();
                return;
            }
            await authClient.signIn.social({
                provider: 'google',
                callbackURL: window.location.href,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Sign in failed.');
            setGoogleLoading(false);
        }
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const { error } = await authClient.signIn.email({ email, password });
            if (error) {
                // Hard gate: an unverified email can't sign in — route to OTP.
                const unverified =
                    error.code === 'EMAIL_NOT_VERIFIED' ||
                    error.status === 403 ||
                    /verif/i.test(error.message ?? '');
                if (unverified) {
                    await authClient.emailOtp.sendVerificationOtp({
                        email,
                        type: 'email-verification',
                    });
                    toast.info('Verify your email to continue — we sent a code.');
                    goTo('verify');
                    return;
                }
                setError(authErrorMessage(error, 'Incorrect email or password.'));
                setLoading(false);
                return;
            }
            // signIn.email updates the reactive session → AuthGuard swaps in,
            // so the spinner deliberately stays up on success.
        } catch (err) {
            // Transport failures REJECT rather than returning {error} — see
            // transportErrorMessage. Without this the button spins forever.
            setError(transportErrorMessage(err));
            setLoading(false);
        }
    };

    return (
        <div>
            <Header
                title="Welcome to cotty"
                sub="Your agentic terminal for machines and servers."
            />

            <button
                type="button"
                onClick={onGoogle}
                disabled={googleLoading}
                className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-xl h-11 px-4 text-[13.5px] font-medium bg-white text-[#1f1f1f] border border-[#d8dee9] transition-[background,box-shadow,transform] duration-100 hover:bg-[#f7f8fa] hover:shadow-[0_2px_8px_rgba(0,0,0,0.18)] active:translate-y-px disabled:opacity-70 disabled:cursor-not-allowed"
            >
                {googleLoading ? (
                    <Loader2 size={16} className="animate-spin text-[#5f6368]" />
                ) : (
                    <GoogleIcon size={18} />
                )}
                <span>{googleLoading ? 'Redirecting…' : 'Continue with Google'}</span>
            </button>

            <Divider label="or" />

            <form onSubmit={onSubmit} className="flex flex-col gap-3">
                <div>
                    <label className={LABEL} htmlFor="si-email">Email</label>
                    <input
                        id="si-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={FIELD_INPUT}
                        placeholder="you@example.com"
                    />
                </div>
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <label className="text-[11.5px] font-medium text-text-dim" htmlFor="si-pass">
                            Password
                        </label>
                        <button
                            type="button"
                            onClick={() => goTo('reset')}
                            className={`text-[11px] ${BTN_LINK}`}
                        >
                            Forgot password?
                        </button>
                    </div>
                    <input
                        id="si-pass"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={FIELD_INPUT}
                        placeholder="••••••••"
                    />
                </div>
                <button type="submit" disabled={loading} className={`mt-1 ${BTN_PRIMARY}`}>
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? 'Signing in…' : 'Sign in'}
                </button>
            </form>

            <AuthError message={error} />

            <p className="mt-5 text-center text-[12.5px] text-text-muted">
                New to cotty?{' '}
                <button type="button" onClick={() => goTo('signup')} className={BTN_LINK}>
                    Create an account
                </button>
            </p>
        </div>
    );
}

// ─── sign up ─────────────────────────────────────────────────────────────────

function SignUpCard({
    email,
    setEmail,
    goTo,
}: {
    email: string;
    setEmail: (v: string) => void;
    goTo: (v: View) => void;
}) {
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setLoading(true);
        try {
            const { error } = await authClient.signUp.email({ name, email, password });
            if (error) {
                setError(authErrorMessage(error, 'Could not create your account.'));
                setLoading(false);
                return;
            }
            // requireEmailVerification + sendOnSignUp already dispatched the OTP.
            toast.success('Account created — check your email for a code.');
            goTo('verify');
        } catch (err) {
            setError(transportErrorMessage(err));
            setLoading(false);
        }
    };

    return (
        <div>
            <Header title="Create your account" sub="Sign up with email and a password." />

            <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-3">
                <div>
                    <label className={LABEL} htmlFor="su-name">Name</label>
                    <input
                        id="su-name"
                        type="text"
                        autoComplete="name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className={FIELD_INPUT}
                        placeholder="Ada Lovelace"
                    />
                </div>
                <div>
                    <label className={LABEL} htmlFor="su-email">Email</label>
                    <input
                        id="su-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={FIELD_INPUT}
                        placeholder="you@example.com"
                    />
                </div>
                <div>
                    <label className={LABEL} htmlFor="su-pass">Password</label>
                    <input
                        id="su-pass"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={FIELD_INPUT}
                        placeholder="At least 8 characters"
                    />
                </div>
                <div>
                    <label className={LABEL} htmlFor="su-confirm">Confirm password</label>
                    <input
                        id="su-confirm"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className={FIELD_INPUT}
                        placeholder="Re-enter password"
                    />
                </div>
                <button type="submit" disabled={loading} className={`mt-1 ${BTN_PRIMARY}`}>
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? 'Creating…' : 'Create account'}
                </button>
            </form>

            <AuthError message={error} />

            <p className="mt-5 text-center text-[12.5px] text-text-muted">
                Already have an account?{' '}
                <button type="button" onClick={() => goTo('signin')} className={BTN_LINK}>
                    Sign in
                </button>
            </p>
        </div>
    );
}

// ─── verify email (OTP) ──────────────────────────────────────────────────────

function VerifyCard({ email, goTo }: { email: string; goTo: (v: View) => void }) {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { remaining, start } = useCooldown();

    const submit = useCallback(
        async (code: string) => {
            setError(null);
            setLoading(true);
            try {
                const { error } = await authClient.emailOtp.verifyEmail({
                    email,
                    otp: code,
                });
                if (error) {
                    setError(authErrorMessage(error, 'Invalid or expired code.'));
                    setOtp('');
                    setLoading(false);
                    return;
                }
                // autoSignInAfterVerification set the session cookie.
                enterApp();
            } catch (err) {
                setError(transportErrorMessage(err));
                setOtp('');
                setLoading(false);
            }
        },
        [email],
    );

    const resend = async () => {
        setError(null);
        try {
            const { error } = await authClient.emailOtp.sendVerificationOtp({
                email,
                type: 'email-verification',
            });
            if (error) {
                setError(authErrorMessage(error));
                return;
            }
            start();
            toast.success('New code sent.');
        } catch (err) {
            setError(transportErrorMessage(err));
        }
    };

    return (
        <div>
            <BackButton onClick={() => goTo('signin')} />
            <Header
                title="Verify your email"
                sub={
                    <>
                        Enter the 6-digit code we sent to{' '}
                        <span className="text-text-dim">{email || 'your email'}</span>.
                    </>
                }
            />

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (otp.length === 6) void submit(otp);
                }}
                className="mt-6 flex flex-col gap-3"
            >
                <OtpField
                    value={otp}
                    onChange={setOtp}
                    onComplete={(v) => void submit(v)}
                    disabled={loading}
                />
                <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className={BTN_PRIMARY}
                >
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? 'Verifying…' : 'Verify'}
                </button>
            </form>

            <AuthError message={error} />

            <p className="mt-5 text-center text-[12.5px] text-text-muted">
                Didn't get it?{' '}
                <button
                    type="button"
                    onClick={resend}
                    disabled={remaining > 0}
                    className={`${BTN_LINK} disabled:no-underline disabled:text-text-faint disabled:cursor-default`}
                >
                    {remaining > 0 ? `Resend in ${remaining}s` : 'Resend code'}
                </button>
            </p>
        </div>
    );
}

// ─── reset password (OTP) ────────────────────────────────────────────────────

function ResetCard({
    email,
    setEmail,
    goTo,
}: {
    email: string;
    setEmail: (v: string) => void;
    goTo: (v: View) => void;
}) {
    const [step, setStep] = useState<'request' | 'reset'>('request');
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { remaining, start } = useCooldown();

    const sendCode = async () => {
        setError(null);
        setLoading(true);
        try {
            const { error } = await authClient.emailOtp.sendVerificationOtp({
                email,
                type: 'forget-password',
            });
            setLoading(false);
            if (error) {
                setError(authErrorMessage(error));
                return;
            }
            start();
            setStep('reset');
            toast.success('If that email exists, a reset code is on its way.');
        } catch (err) {
            setError(transportErrorMessage(err));
            setLoading(false);
        }
    };

    const reset = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        setLoading(true);
        try {
            const { error } = await authClient.emailOtp.resetPassword({
                email,
                otp,
                password,
            });
            setLoading(false);
            if (error) {
                setError(authErrorMessage(error, 'Invalid or expired code.'));
                return;
            }
            toast.success('Password updated — sign in with your new password.');
            goTo('signin');
        } catch (err) {
            setError(transportErrorMessage(err));
            setLoading(false);
        }
    };

    return (
        <div>
            <BackButton onClick={() => goTo('signin')} />
            <Header
                title="Reset your password"
                sub={
                    step === 'request'
                        ? 'Enter your email and we will send a reset code.'
                        : 'Enter the code and choose a new password.'
                }
            />

            {step === 'request' ? (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void sendCode();
                    }}
                    className="mt-6 flex flex-col gap-3"
                >
                    <div>
                        <label className={LABEL} htmlFor="rp-email">Email</label>
                        <input
                            id="rp-email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={FIELD_INPUT}
                            placeholder="you@example.com"
                        />
                    </div>
                    <button type="submit" disabled={loading} className={BTN_PRIMARY}>
                        {loading && <Loader2 size={16} className="animate-spin" />}
                        {loading ? 'Sending…' : 'Send reset code'}
                    </button>
                </form>
            ) : (
                <form onSubmit={reset} className="mt-6 flex flex-col gap-3">
                    <div>
                        <label className={LABEL}>Code</label>
                        <OtpField value={otp} onChange={setOtp} disabled={loading} />
                    </div>
                    <div>
                        <label className={LABEL} htmlFor="rp-pass">New password</label>
                        <input
                            id="rp-pass"
                            type="password"
                            autoComplete="new-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={FIELD_INPUT}
                            placeholder="At least 8 characters"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading || otp.length !== 6}
                        className={BTN_PRIMARY}
                    >
                        {loading && <Loader2 size={16} className="animate-spin" />}
                        {loading ? 'Updating…' : 'Update password'}
                    </button>
                    <button
                        type="button"
                        onClick={sendCode}
                        disabled={remaining > 0}
                        className={`text-center text-[12px] ${BTN_LINK} disabled:no-underline disabled:text-text-faint disabled:cursor-default`}
                    >
                        {remaining > 0 ? `Resend in ${remaining}s` : 'Resend code'}
                    </button>
                </form>
            )}

            <AuthError message={error} />
        </div>
    );
}

// ─── small shared bits ───────────────────────────────────────────────────────

function Header({
    title,
    sub,
}: {
    title: string;
    sub: React.ReactNode;
}) {
    return (
        <div className="flex flex-col items-center text-center">
            <h1 className="text-[22px] font-medium tracking-tight text-text">{title}</h1>
            <p className="mt-1.5 text-[13px] text-text-muted leading-relaxed">{sub}</p>
        </div>
    );
}

function Divider({ label }: { label: string }) {
    return (
        <div className="my-5 flex items-center gap-3 text-text-faint">
            <div className="flex-1 h-px bg-border-faint" />
            <span className="text-[10.5px] uppercase tracking-[0.08em]">{label}</span>
            <div className="flex-1 h-px bg-border-faint" />
        </div>
    );
}

function BackButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="absolute left-4 top-4 grid place-items-center w-7 h-7 rounded-md text-text-muted hover:text-text hover:bg-bg-hover transition-colors"
            aria-label="Back to sign in"
        >
            <ArrowLeft size={15} />
        </button>
    );
}

// 30s resend cooldown now lives in authShared (shared with /delete-account).
